import { useState, useEffect, type FC } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Tooltip } from 'flowbite-react';
import { HiQuestionMarkCircle, HiFilter, HiBookmark } from 'react-icons/hi';
import type { FilterGroup } from '../../types/filter';
import {
  createEmptyFilterGroup,
  createEmptyFilterCondition,
  countActiveConditions,
} from '../../types/filter';
import { ConditionGroup } from './ConditionGroup';

interface FilterBuilderProps {
  show: boolean;
  onClose: () => void;
  initialFilter: FilterGroup | null;
  onApply: (filter: FilterGroup | null) => void;
  onOpenSavePreset: () => void;
  isLoading?: boolean;
}

export const FilterBuilder: FC<FilterBuilderProps> = ({
  show,
  onClose,
  initialFilter,
  onApply,
  onOpenSavePreset,
  isLoading = false,
}) => {
  // Local draft state of the filter tree while modal is open
  const [draftFilter, setDraftFilter] = useState<FilterGroup>(() => {
    return (
      initialFilter ||
      createEmptyFilterGroup('and', [createEmptyFilterCondition()])
    );
  });

  // Sync draft whenever modal opens with initialFilter
  useEffect(() => {
    if (show) {
      if (initialFilter && initialFilter.children.length > 0) {
        setDraftFilter(JSON.parse(JSON.stringify(initialFilter)));
      } else {
        setDraftFilter(
          createEmptyFilterGroup('and', [createEmptyFilterCondition()])
        );
      }
    }
  }, [show, initialFilter]);

  const handleReset = () => {
    setDraftFilter(createEmptyFilterGroup('and', [createEmptyFilterCondition()]));
  };

  const handleApply = () => {
    const activeCount = countActiveConditions(draftFilter);
    if (activeCount === 0) {
      onApply(null);
    } else {
      onApply(draftFilter);
    }
    onClose();
  };

  const activeCount = countActiveConditions(draftFilter);

  return (
    <Modal show={show} onClose={onClose} size="4xl" dismissible>
      <ModalHeader>
        <div className="flex items-center gap-2">
          <HiFilter className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          <span className="font-semibold text-gray-900 dark:text-white">
            Filter konfigurieren
          </span>
          <Tooltip
            content="Erstellen Sie komplexe Filterabfragen mit AND/OR-Gruppen für Mitarbeiter, Verträge, Kunden und Standorte."
            placement="right"
          >
            <HiQuestionMarkCircle className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
          </Tooltip>
        </div>
      </ModalHeader>

      <ModalBody className="space-y-4">
        <div className="flex items-center justify-between border-b border-gray-200 pb-2 dark:border-gray-700">
          <p className="text-xs font-medium text-gray-600 dark:text-gray-300">
            In dieser Ansicht Datensätze anzeigen, die folgende Kriterien erfüllen:
          </p>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {activeCount} {activeCount === 1 ? 'Bedingung aktiv' : 'Bedingungen aktiv'}
          </span>
        </div>

        {/* Recursive condition group builder */}
        <div className="max-h-[60vh] overflow-y-auto pr-1">
          <ConditionGroup
            group={draftFilter}
            depth={0}
            onUpdate={setDraftFilter}
            disabled={isLoading}
          />
        </div>
      </ModalBody>

      <ModalFooter className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 pt-3 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <Button
            color="light"
            size="xs"
            onClick={handleReset}
            disabled={isLoading}
          >
            Zurücksetzen
          </Button>
          <Button
            color="light"
            size="xs"
            onClick={onOpenSavePreset}
            disabled={isLoading || activeCount === 0}
            className="flex items-center gap-1.5"
          >
            <HiBookmark className="mr-1 h-3.5 w-3.5 text-blue-600" />
            Als Vorlage speichern...
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Button color="gray" size="sm" onClick={onClose} disabled={isLoading}>
            Abbrechen
          </Button>
          <Button
            color="blue"
            size="sm"
            onClick={handleApply}
            disabled={isLoading}
          >
            Filter anwenden ({activeCount})
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};
